/**
 * Shared builder for the Packages list query params.
 *
 * The Packages filter modal, the Packages page and the in-table column filter all
 * hit `GET package/user` with the same three params. They used to carry three
 * separate copies of this logic, which is how CWS-1219 happened: two of the copies
 * resolved `package_status` with `if (active) … else if (inactive)` and so hid every
 * inactive package when both boxes were ticked. One builder, one behaviour.
 */
import { FilterParams } from '../types';

/** UI wash-type labels, keyed by their separator-normalized form. */
const WASH_TYPE_API_VALUES: Record<string, string> = {
  single_wash: 'single_wash',
  monthly_membership: 'monthly_membership',
  detailing: 'detailing',
  add_on: 'add_on',
};

export interface PackageFilterSelection {
  /** `active` / `inactive`, in any order. */
  statuses?: string[];
  /** Wash-type labels, hyphen- or space-separated (`single-wash`, `single wash`). */
  carWashTypes?: string[];
  /** Location ids. */
  locations?: string[];
}

/**
 * `'true'` for active-only, `'false'` for inactive-only, `undefined` for both or
 * neither — omitting the param is what makes the API return every status.
 */
export function resolvePackageStatusParam(statuses: string[] = []): string | undefined {
  const hasActive = statuses.includes('active');
  const hasInactive = statuses.includes('inactive');
  if (hasActive && !hasInactive) {
    return 'true';
  }
  if (hasInactive && !hasActive) {
    return 'false';
  }
  return undefined;
}

/**
 * Map a UI wash-type label to its API token, accepting either separator style.
 * Unrecognized values pass through untouched so callers can send custom types.
 */
export function mapWashTypeToApiValue(type: string): string {
  const normalized = String(type)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return WASH_TYPE_API_VALUES[normalized] ?? type;
}

/** Build the `GET package/user` query params from a set of filter selections. */
export function buildPackageFilterParams(selection: PackageFilterSelection): FilterParams {
  const params: FilterParams = {};

  const packageStatus = resolvePackageStatusParam(selection.statuses);
  if (packageStatus !== undefined) {
    params.package_status = packageStatus;
  }

  if (selection.carWashTypes?.length) {
    params.wash_types = selection.carWashTypes.map(mapWashTypeToApiValue).join(',');
  }

  if (selection.locations?.length) {
    params.locations = selection.locations.join(',');
  }

  return params;
}
